import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    UserPlus,
    KeyRound,
    Power,
    Pencil,
    Loader2,
    X,
    Check,
    UserCog,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useForm } from "../components/forms/useForm";
import { Field, TextInput, SelectInput } from "../components/forms/Field";
import EmptyState from "../components/EmptyState";
import {
    required,
    email as emailV,
    phone as phoneV,
    passwordMin,
    compose,
} from "../lib/validation";
import dict from "../i18n/es-MX";

const ROLES = ["admin", "manager", "operator"];

function Modal({ open, onClose, title, children, footer }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <div className="relative w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                    <h2 className="font-display text-xl font-bold">{title}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="modal-close"
                        className="text-slate-400 hover:text-slate-100 p-1"
                    >
                        <X size={18} />
                    </button>
                </div>
                <div className="p-5">{children}</div>
                {footer && (
                    <div className="px-5 py-4 border-t border-slate-800 flex justify-end gap-2">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

function RoleBadge({ role }) {
    const map = {
        admin: "bg-brand-orange/20 text-brand-orange border-brand-orange/40",
        manager: "bg-sky-500/15 text-sky-300 border-sky-500/40",
        operator: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    };
    return (
        <span
            data-testid={`role-badge-${role}`}
            className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider border ${map[role] || ""}`}
        >
            {dict.roles[role] || role}
        </span>
    );
}

function StatusBadge({ active }) {
    return active ? (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
            <Check size={10} /> {dict.common.active}
        </span>
    ) : (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider bg-slate-700/40 text-slate-400 border border-slate-700">
            {dict.common.inactive}
        </span>
    );
}

export default function Users() {
    const { user: currentUser } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [editingUser, setEditingUser] = useState(null);
    const [creating, setCreating] = useState(false);
    const [resetUser, setResetUser] = useState(null);
    const [toast, setToast] = useState(null);

    const userForm = useForm({
        initial: { full_name: "", email: "", phone: "", role: "operator", password: "" },
        validators: {
            full_name: required,
            email: (v) => (editingUser ? null : compose(required, emailV)(v)),
            phone: phoneV,
            role: required,
            password: (v) => (editingUser ? null : compose(required, passwordMin)(v)),
        },
    });

    const resetForm = useForm({
        initial: { password: "" },
        validators: { password: compose(required, passwordMin) },
    });

    const showToast = useCallback((msg, type = "success") => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 2500);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await api.get("/users");
            setUsers(res.data);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(
        () =>
            users.filter((u) => {
                if (roleFilter && u.role !== roleFilter) return false;
                if (statusFilter === "active" && !u.is_active) return false;
                if (statusFilter === "inactive" && u.is_active) return false;
                return true;
            }),
        [users, roleFilter, statusFilter],
    );

    const openCreate = () => {
        userForm.reset({ full_name: "", email: "", phone: "", role: "operator", password: "" });
        setEditingUser(null);
        setCreating(true);
    };

    const openEdit = (u) => {
        setEditingUser(u);
        setCreating(false);
        userForm.reset({
            full_name: u.full_name || "",
            email: u.email,
            phone: u.phone || "",
            role: u.role,
            password: "",
        });
    };

    const closeForm = () => {
        setCreating(false);
        setEditingUser(null);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        userForm.handleSubmit(async (v) => {
            try {
                if (editingUser) {
                    await api.patch(`/users/${editingUser.id}`, {
                        full_name: v.full_name.trim(),
                        phone: v.phone || null,
                        role: v.role,
                    });
                    showToast(dict.users.updated);
                } else {
                    await api.post("/users", {
                        email: v.email.trim().toLowerCase(),
                        password: v.password,
                        full_name: v.full_name.trim(),
                        phone: v.phone || null,
                        role: v.role,
                    });
                    showToast(dict.users.created);
                }
                await load();
                closeForm();
            } catch (err) {
                showToast(formatApiError(err), "error");
            }
        });
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
            await load();
            showToast(u.is_active ? dict.users.deactivated : dict.users.activated);
        } catch (err) {
            showToast(formatApiError(err), "error");
        }
    };

    const handleResetSubmit = (e) => {
        e.preventDefault();
        resetForm.handleSubmit(async (v) => {
            try {
                await api.post(`/users/${resetUser.id}/reset-password`, { password: v.password });
                showToast(dict.users.passwordChanged);
                setResetUser(null);
                resetForm.reset({ password: "" });
            } catch (err) {
                showToast(formatApiError(err), "error");
            }
        });
    };

    return (
        <div data-testid="users-page" className="space-y-6">
            <header className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
                        {dict.users.title}
                    </h1>
                    <p className="mt-2 text-slate-400 text-sm">{dict.users.subtitle}</p>
                </div>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="add-user-button"
                    className="inline-flex items-center gap-2 h-11 px-4 rounded-lg bg-brand-orange hover:bg-orange-400 active:bg-orange-600 text-slate-950 font-semibold uppercase tracking-wider text-sm transition"
                >
                    <UserPlus size={18} />
                    {dict.users.addUser}
                </button>
            </header>

            {/* Filters */}
            <div className="flex gap-3 flex-wrap">
                <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">
                        {dict.users.filters.role}
                    </label>
                    <select
                        data-testid="filter-role"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"
                    >
                        <option value="">{dict.common.all}</option>
                        {ROLES.map((r) => (
                            <option key={r} value={r}>
                                {dict.roles[r]}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">
                        {dict.users.filters.status}
                    </label>
                    <select
                        data-testid="filter-status"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="h-10 px-3 rounded-lg bg-slate-950 border border-slate-700 text-sm"
                    >
                        <option value="">{dict.common.all}</option>
                        <option value="active">{dict.common.active}</option>
                        <option value="inactive">{dict.common.inactive}</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-slate-800 overflow-hidden bg-slate-900/40">
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm" data-testid="users-table">
                        <thead className="bg-slate-900/80 text-xs uppercase tracking-wider text-slate-400">
                            <tr>
                                <th className="text-left px-4 py-3">{dict.users.columns.name}</th>
                                <th className="text-left px-4 py-3">{dict.users.columns.email}</th>
                                <th className="text-left px-4 py-3 hidden sm:table-cell">{dict.users.columns.phone}</th>
                                <th className="text-left px-4 py-3">{dict.users.columns.role}</th>
                                <th className="text-left px-4 py-3">{dict.users.columns.status}</th>
                                <th className="text-right px-4 py-3">{dict.users.columns.actions}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-slate-500">
                                        <Loader2 className="animate-spin inline" size={18} />
                                    </td>
                                </tr>
                            )}
                            {!loading && error && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-10 text-center text-red-400">
                                        {error}
                                    </td>
                                </tr>
                            )}
                            {!loading && !error && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-0 py-0">
                                        <EmptyState icon={UserCog} title={dict.users.empty} testid="users-empty"/>
                                    </td>
                                </tr>
                            )}
                            {!loading &&
                                filtered.map((u) => (
                                    <tr
                                        key={u.id}
                                        data-testid={`user-row-${u.id}`}
                                        className="border-t border-slate-800 hover:bg-slate-900/60"
                                    >
                                        <td className="px-4 py-3 font-medium text-slate-100">
                                            {u.full_name}
                                            {u.id === currentUser?.id && (
                                                <span className="ml-2 text-[10px] uppercase tracking-wider text-brand-orange">
                                                    ({dict.users.you})
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-slate-300">{u.email}</td>
                                        <td className="px-4 py-3 text-slate-400 hidden sm:table-cell">{u.phone || dict.common.none}</td>
                                        <td className="px-4 py-3">
                                            <RoleBadge role={u.role} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <StatusBadge active={u.is_active} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    type="button"
                                                    onClick={() => openEdit(u)}
                                                    title={dict.users.editUser}
                                                    data-testid={`edit-user-${u.id}`}
                                                    className="p-2 rounded hover:bg-slate-800 text-slate-300 hover:text-slate-50"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setResetUser(u);
                                                        resetForm.reset({ password: "" });
                                                    }}
                                                    title={dict.users.resetPassword}
                                                    data-testid={`reset-pw-${u.id}`}
                                                    className="p-2 rounded hover:bg-slate-800 text-slate-300 hover:text-brand-orange"
                                                >
                                                    <KeyRound size={16} />
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => toggleActive(u)}
                                                    title={u.is_active ? dict.users.deactivate : dict.users.activate}
                                                    data-testid={`toggle-active-${u.id}`}
                                                    disabled={u.id === currentUser?.id && u.is_active}
                                                    className={`p-2 rounded hover:bg-slate-800 ${
                                                        u.is_active
                                                            ? "text-red-400 hover:text-red-300"
                                                            : "text-emerald-400 hover:text-emerald-300"
                                                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                                                >
                                                    <Power size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Create / edit modal */}
            <Modal
                open={creating || !!editingUser}
                onClose={closeForm}
                title={editingUser ? dict.users.editUser : dict.users.addUser}
                footer={
                    <>
                        <button
                            type="button"
                            onClick={closeForm}
                            className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm"
                        >
                            {dict.common.cancel}
                        </button>
                        <button
                            type="submit"
                            form="user-form"
                            disabled={userForm.submitting}
                            data-testid="user-form-submit"
                            className="h-10 px-4 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {userForm.submitting && <Loader2 className="animate-spin" size={16} />}
                            {userForm.submitting ? dict.common.saving : dict.common.save}
                        </button>
                    </>
                }
            >
                <form id="user-form" onSubmit={handleSubmit} noValidate className="space-y-4">
                    <Field
                        label={dict.users.fields.fullName}
                        required
                        htmlFor="full_name"
                        error={userForm.errors.full_name}
                    >
                        <TextInput
                            {...userForm.field("full_name")}
                            error={userForm.errors.full_name}
                            data-testid="form-full-name"
                        />
                    </Field>
                    <Field
                        label={dict.users.fields.email}
                        required
                        htmlFor="email"
                        error={userForm.errors.email}
                    >
                        <TextInput
                            type="email"
                            {...userForm.field("email")}
                            disabled={!!editingUser}
                            error={userForm.errors.email}
                            data-testid="form-email"
                        />
                    </Field>
                    <Field
                        label={dict.users.fields.phone}
                        htmlFor="phone"
                        error={userForm.errors.phone}
                    >
                        <TextInput
                            {...userForm.field("phone")}
                            error={userForm.errors.phone}
                            data-testid="form-phone"
                        />
                    </Field>
                    <Field
                        label={dict.users.fields.role}
                        required
                        htmlFor="role"
                        error={userForm.errors.role}
                    >
                        <SelectInput
                            {...userForm.field("role")}
                            error={userForm.errors.role}
                            options={ROLES.map((r) => ({ value: r, label: dict.roles[r] }))}
                            data-testid="form-role"
                        />
                    </Field>
                    {!editingUser && (
                        <Field
                            label={dict.users.fields.password}
                            required
                            htmlFor="password"
                            error={userForm.errors.password}
                            hint={userForm.errors.password ? null : dict.users.fields.passwordHint}
                        >
                            <TextInput
                                type="password"
                                {...userForm.field("password")}
                                error={userForm.errors.password}
                                data-testid="form-password"
                            />
                        </Field>
                    )}
                </form>
            </Modal>

            {/* Reset password modal */}
            <Modal
                open={!!resetUser}
                onClose={() => {
                    setResetUser(null);
                    resetForm.reset({ password: "" });
                }}
                title={`${dict.users.resetPassword} — ${resetUser?.full_name || ""}`}
                footer={
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                setResetUser(null);
                                resetForm.reset({ password: "" });
                            }}
                            className="h-10 px-4 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm"
                        >
                            {dict.common.cancel}
                        </button>
                        <button
                            type="submit"
                            form="reset-form"
                            disabled={resetForm.submitting}
                            data-testid="reset-pw-submit"
                            className="h-10 px-4 rounded-lg bg-brand-orange hover:bg-orange-400 text-slate-950 font-semibold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {resetForm.submitting && <Loader2 className="animate-spin" size={16} />}
                            {resetForm.submitting ? dict.common.saving : dict.common.confirm}
                        </button>
                    </>
                }
            >
                <form id="reset-form" onSubmit={handleResetSubmit} noValidate>
                    <Field
                        label={dict.users.fields.newPassword}
                        required
                        htmlFor="password"
                        error={resetForm.errors.password}
                        hint={resetForm.errors.password ? null : dict.users.fields.passwordHint}
                    >
                        <TextInput
                            type="password"
                            {...resetForm.field("password")}
                            error={resetForm.errors.password}
                            data-testid="reset-pw-input"
                        />
                    </Field>
                </form>
            </Modal>

            {toast && (
                <div
                    data-testid="toast"
                    className={`fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2 rounded-md text-sm shadow-lg z-50 ${
                        toast.type === "error"
                            ? "bg-red-700 text-white"
                            : "bg-emerald-700 text-white"
                    }`}
                >
                    {toast.msg}
                </div>
            )}
        </div>
    );
}
